import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Users, MessageSquare, FileText, TrendingUp, Activity, HelpCircle } from "lucide-react";
import { motion } from "framer-motion";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function StatCard({ icon: Icon, label, value, color, delay }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.3 }}
      className="bg-zinc-900/40 border border-zinc-800/50 rounded-2xl p-5 hover:border-zinc-700 transition-all"
    >
      <div className="flex items-center justify-between mb-3">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${color}`}>
          <Icon size={18} />
        </div>
      </div>
      <p data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`} className="text-2xl font-bold text-zinc-50 tracking-tight" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>
        {value}
      </p>
      <p className="text-xs text-zinc-500 mt-0.5">{label}</p>
    </motion.div>
  );
}

function MiniChart({ data }) {
  if (!data || data.length === 0) return null;
  const maxVal = Math.max(...data.map(d => d.messages), 1);

  return (
    <div className="flex items-end gap-1.5 h-32">
      {data.map((d, i) => {
        const height = Math.max((d.messages / maxVal) * 100, 4);
        const isToday = i === data.length - 1;
        return (
          <div key={d.date} className="flex-1 flex flex-col items-center gap-1">
            <motion.div
              initial={{ height: 0 }}
              animate={{ height: `${height}%` }}
              transition={{ delay: i * 0.03, duration: 0.4 }}
              className={`w-full rounded-t-md min-h-[4px] ${
                isToday ? "bg-emerald-500" : "bg-zinc-700 hover:bg-zinc-600"
              } transition-colors`}
              title={`${d.date}: ${d.messages} mensajes`}
            />
            {(i === 0 || i === data.length - 1 || i === Math.floor(data.length / 2)) && (
              <span className="text-[8px] text-zinc-600">
                {d.date.slice(5)}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default function AnalyticsView() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchAnalytics = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/analytics`);
      setData(res.data);
    } catch (e) {
      console.error("Error fetching analytics", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-6 h-6 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center text-zinc-500 py-12">
        No se pudieron cargar las analíticas.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard icon={Users} label="Alumnos" value={data.total_users} color="bg-emerald-500/10 text-emerald-400" delay={0} />
        <StatCard icon={MessageSquare} label="Mensajes totales" value={data.total_messages} color="bg-blue-500/10 text-blue-400" delay={0.05} />
        <StatCard icon={Activity} label="Sesiones" value={data.total_sessions} color="bg-amber-500/10 text-amber-400" delay={0.1} />
        <StatCard icon={FileText} label="Documentos" value={data.total_documents} color="bg-purple-500/10 text-purple-400" delay={0.15} />
      </div>

      {/* Active users */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-zinc-900/40 border border-zinc-800/50 rounded-2xl p-5"
      >
        <div className="flex items-center gap-2 mb-1">
          <TrendingUp size={14} className="text-emerald-400" />
          <h3 className="text-xs font-semibold text-zinc-400 uppercase tracking-wider">
            Usuarios activos (7 días)
          </h3>
        </div>
        <p data-testid="stat-active-users" className="text-3xl font-bold text-zinc-50" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>
          {data.active_users_7d}
        </p>
      </motion.div>

      {/* Chart */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.25 }}
        className="bg-zinc-900/40 border border-zinc-800/50 rounded-2xl p-6"
      >
        <h3
          className="text-sm font-semibold text-zinc-300 mb-4 tracking-wide uppercase"
          style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}
        >
          Mensajes por día (14 días)
        </h3>
        <MiniChart data={data.daily_messages} />
      </motion.div>

      {/* Top Questions */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="bg-zinc-900/40 border border-zinc-800/50 rounded-2xl p-6"
      >
        <div className="flex items-center gap-2 mb-4">
          <HelpCircle size={14} className="text-emerald-400" />
          <h3
            className="text-sm font-semibold text-zinc-300 tracking-wide uppercase"
            style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}
          >
            Preguntas recientes
          </h3>
        </div>
        {data.recent_questions.length === 0 ? (
          <p className="text-xs text-zinc-600">No hay preguntas aún.</p>
        ) : (
          <div className="space-y-2">
            {data.recent_questions.map((q, i) => (
              <div
                key={i}
                data-testid={`recent-question-${i}`}
                className="flex items-start gap-2.5 px-3 py-2.5 bg-zinc-950/40 rounded-xl border border-zinc-800/30"
              >
                <span className="text-emerald-500 text-xs font-bold mt-0.5 flex-shrink-0">
                  {i + 1}.
                </span>
                <p className="text-xs text-zinc-400 leading-relaxed">{q}</p>
              </div>
            ))}
          </div>
        )}
      </motion.div>
    </div>
  );
}
